/**
 * MSAL bootstrap — SysOps Panel.
 *
 * Plain-browser, mypcb-tenant Entra SSO. SysOps users are bank staff
 * (NOT pcbportal external IDs), so we point at tenant
 * `0f60eb50-7ea7-4db3-b65b-0bc945f33568`.
 *
 * Token resource: the `pcbaccountopeningapi` Function App's exposed scope.
 * Per design doc (wave-8.1-sysops-panel-deploy.md), Ron registers
 * `pcb-sysops-panel-spa` in the mypcb tenant and grants delegated access to
 * the API's `access_as_user` (or equivalent) scope.
 */

import {
  PublicClientApplication,
  LogLevel,
  type Configuration,
} from '@azure/msal-browser';

const clientId =
  (import.meta.env.VITE_AAD_CLIENT_ID as string | undefined) ?? '';
const tenantId =
  (import.meta.env.VITE_AAD_TENANT_ID as string | undefined) ??
  '0f60eb50-7ea7-4db3-b65b-0bc945f33568';

/** Graph User.Read for login. The PCB tenant blocks user-consent for custom
 *  APIs, so we cannot request api://<backend> without admin consent; instead the
 *  crypto-validatable token is sourced from the ID token in getMsalToken (no
 *  consent needed). Bill 2026-06-18. */
export const API_SCOPES = ['User.Read', 'openid', 'profile', 'email'];

export const MSAL_CONFIG: Configuration = {
  auth: {
    clientId: clientId || '00000000-0000-0000-0000-000000000000',
    authority: `https://login.microsoftonline.com/${tenantId}`,
    // Root mount on the dedicated pcb-sysops-panel SWA (gray-meadow). The
    // legacy '/sys-ops/' sub-path was retired 2026-05-10 — see memory
    // feedback_swa_deploy_target_unbreakable_rule_20260510.md. Note:
    // matching redirect URI MUST also be registered on the Entra app reg
    // (`pcb-sysops-panel-spa`); Ron action if not already there.
    redirectUri:
      typeof window !== 'undefined'
        ? `${window.location.origin}/`
        : '/',
    postLogoutRedirectUri:
      typeof window !== 'undefined'
        ? `${window.location.origin}/`
        : '/',
  },
  cache: {
    cacheLocation: 'sessionStorage',
  },
  system: {
    loggerOptions: {
      logLevel: LogLevel.Warning,
      piiLoggingEnabled: false,
      loggerCallback: (level, message) => {
        if (level <= LogLevel.Warning) {
          // eslint-disable-next-line no-console
          console.warn('[sysops-msal]', message);
        }
      },
    },
  },
};

export const msalInstance = new PublicClientApplication(MSAL_CONFIG);

let msalReadyPromise: Promise<void> | null = null;
export function ensureMsalReady(): Promise<void> {
  if (!msalReadyPromise) {
    msalReadyPromise = (async () => {
      await msalInstance.initialize();
      await msalInstance.handleRedirectPromise({ navigateToLoginRequestUrl: false });
    })();
  }
  return msalReadyPromise;
}

/**
 * Acquire an access token for the v3 Function. Silent first, popup
 * fallback. Popup keeps the SPA alive (mirrors ROP-teams behavior).
 */
/** One token request at a time — parallel silent calls raced into interaction_in_progress and sent
 *  requests with no token (2026-10-06, same fix as the admin panel). */
let inFlight: Promise<string> | null = null;

export function getMsalToken(scopes: string[] = API_SCOPES): Promise<string> {
  if (!inFlight) {
    inFlight = acquireIdToken(scopes).finally(() => { inFlight = null; });
  }
  return inFlight;
}

/** Seconds left on an ID token, from its exp claim (0 when unreadable). */
function secondsLeft(claims: Record<string, unknown> | undefined): number {
  const exp = typeof claims?.exp === 'number' ? claims.exp : 0;
  return exp - Math.floor(Date.now() / 1000);
}

async function acquireIdToken(scopes: string[]): Promise<string> {
  await ensureMsalReady();

  const accounts = msalInstance.getAllAccounts();
  const firstAccount = accounts[0];

  if (firstAccount) {
    try {
      let result = await msalInstance.acquireTokenSilent({
        scopes,
        account: firstAccount,
      });
      // Send the ID token (tenant-signed, no Graph nonce) so the backend can
      // crypto-validate it without an api-scope admin consent. Bill 2026-06-18.
      // MSAL only checks the Graph token's expiry, not the ID token's — refresh an ID token with
      // under 5 minutes left, or the API rejects it (2026-10-06).
      if (secondsLeft(result.idTokenClaims as Record<string, unknown> | undefined) < 300) {
        result = await msalInstance.acquireTokenSilent({ scopes, account: firstAccount, forceRefresh: true });
      }
      return result.idToken;
    } catch {
      // Silent failed — interactive redirect below.
    }
  }

  // Interactive sign-in via full-page redirect (NOT loginPopup, which the
  // browser popup-blocker kills when fired on load without a user gesture).
  // Reuses the Edge/Windows work session; handleRedirectPromise() in
  // ensureMsalReady() completes the round-trip on return, then the next call
  // resolves silently from the cached account.
  await msalInstance.loginRedirect({ scopes });
  return new Promise<string>(() => { /* unreachable — page redirected */ });
}
